import React from 'react';

function amtLabel(value, type, currency = 'EUR') {
  if (value == null) return null;
  const t = String(type || '').toLowerCase();
  if (t === 'percentage' || t === 'percent') return `${Number(value)}% of booking`;
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(value)); }
  catch { return `${currency} ${Number(value).toFixed(2)}`; }
}

export default function RulesSummary({ feePolicy, editRule, cancellationRules = [], currency = 'EUR', maxDateShiftDays }) {
  const fee = feePolicy || null;
  const edit = editRule || null;
  const showAny = !!(fee || edit);
  const dateShiftDays = (maxDateShiftDays != null) ? maxDateShiftDays : (edit?.max_date_shift_days != null ? edit.max_date_shift_days : null);

  return (
    <div className="space-y-3 text-sm">
      {/* Fees & Prepayment */}
      {fee && (
        <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-4 h-4 text-blue-600" viewBox="0 0 24 24" fill="currentColor"><path d="M12 1a4 4 0 00-4 4v2H5a1 1 0 00-1 1v11a3 3 0 003 3h10a3 3 0 003-3V8a1 1 0 00-1-1h-3V5a4 4 0 00-4-4zm-2 6V5a2 2 0 114 0v2h-4z"/></svg>
            <div className="font-medium text-blue-900">Fees & Prepayment</div>
          </div>
          <div className="grid md:grid-cols-2 gap-2 text-blue-900">
            <div>
              {fee.full_prepayment_required ? (
                <div>Prepayment: Full prepayment required</div>
              ) : (
                fee.prepayment_amount != null && (
                  <div>Prepayment: {amtLabel(fee.prepayment_amount, fee.prepayment_type, currency)}</div>
                )
              )}
              {Number(fee.charge_before_value) > 0 && (
                <div>Charged: {fee.charge_before_value} {String(fee.charge_before_unit || 'hours')} before start</div>
              )}
            </div>
            <div>
              {fee.no_show_fee_amount != null && Number(fee.no_show_fee_amount) > 0 && (
                <div>No-show fee: {amtLabel(fee.no_show_fee_amount, fee.no_show_fee_type, currency)}{fee.no_show_timeout_value ? ` — applies ${fee.no_show_timeout_value} ${String(fee.no_show_timeout_unit || 'minutes')} after start` : ''}</div>
              )}
            </div>
          </div>
          {Array.isArray(cancellationRules) && cancellationRules.length > 0 && (
            <div className="mt-3 text-blue-900">
              <div className="font-medium mb-1">Cancellation rules</div>
              <ul className="list-disc ml-5 space-y-1">
                {cancellationRules.map((r, i) => (
                  <li key={i}>
                    {(r.label) ? r.label : `${r.time_value || r.time_before || ''} ${String(r.time_unit || r.time_before_unit || '').trim()}`}
                    {(r.amount != null)
                      ? ` — Fee: ${amtLabel(Number(r.amount), r.amount_type, currency)}`
                      : (r.fee_amount != null ? ` — Fee: ${amtLabel(Number(r.fee_amount), r.fee_type, currency)}` : '')}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Edit Rules */}
      {edit && (
        <div className="rounded-lg border border-purple-100 bg-purple-50 p-3">
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-4 h-4 text-purple-700" viewBox="0 0 24 24" fill="currentColor"><path d="M4 21h4l10-10-4-4L4 17v4zm14.7-12.3a1 1 0 000-1.4l-2-2a1 1 0 00-1.4 0l-1 1 3.4 3.4 1-1z"/></svg>
            <div className="font-medium text-purple-900">Edit Rules</div>
          </div>
          <div className="grid md:grid-cols-2 gap-2 text-purple-900">
            {dateShiftDays != null && (
              <div>Date can shift up to {Number(dateShiftDays)} day(s)</div>
            )}
            {(edit.edit_cutoff_value || edit.edit_cutoff_unit) && (
              <div>Cutoff: {edit.edit_cutoff_value || ''} {String(edit.edit_cutoff_unit || '').trim()}</div>
            )}
            {Number(edit.edit_fee_amount) > 0 && (
              <div>Edit fee: {amtLabel(edit.edit_fee_amount, edit.edit_fee_type, currency)}</div>
            )}
            {Number(edit.max_edits_per_reservation) > 0 && (
              <div>Max edits: {edit.max_edits_per_reservation}</div>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-2 text-[12px]">
            {[
              { k: 'allow_party_size_changes', label: 'Party size change' },
              { k: 'allow_switch_service', label: 'Switch service' },
              { k: 'allow_switch_class', label: 'Switch class' },
              { k: 'allow_change_room_type', label: 'Change room type' },
              { k: 'allow_change_seating_area', label: 'Change seating area' },
            ].map(opt => (
              <span key={opt.k} className={`inline-flex items-center px-2 py-0.5 rounded-full border ${edit?.[opt.k] ? 'bg-green-100 text-green-800 border-green-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                {opt.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {!showAny && (
        <div className="text-sm text-gray-600">No specific policies provided for this item.</div>
      )}
    </div>
  );
}
